import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { useContract } from '../hooks/useContract';
import { formatNumber, formatUserId, copyToClipboard, formatTimeAgo } from '../utils/formatters';
import { PACKAGE_INFO, ACTIVITY_TYPES } from '../config/contract';
import Loader from './Loader';
import { getContractReadOnly } from '../utils/web3';


const Dashboard = ({ 
  userId, userData, packages, incomeBreakdown, claimableIncome, 
  referrals, activities, poolEligibility, royaltyEligible,
  usdtBalance, onRefresh, addToast, onShowActivities, onShowTelegram
}) => {
  const [isClaiming, setIsClaiming] = useState(false);
  const [incomeTab, setIncomeTab] = useState('total');
  const { claimAllIncome, isLoading } = useContract();
  const [levelCounts, setLevelCounts] = useState([]);

  const activePackages = packages.filter(p => p.isActive);
  const highestPackage = activePackages.length > 0 ? activePackages[activePackages.length - 1] : null;
  const recentActivities = activities.slice(0, 5);
  const totalTeam = levelCounts.reduce((a, b) => a + b, 0);

  useEffect(() => {
  const fetchLevelCounts = async () => {
    if (!userId || activePackages.length === 0) return;
    try {
      const contract = getContractReadOnly();
      const lowestPkgId = activePackages[0].id;  // Use FIRST package, not last
      const counts = await contract.getMatrixLevelCounts(userId, lowestPkgId);
      setLevelCounts(counts.map(c => c.toNumber()));
    } catch (error) {
      console.error('Error fetching level counts:', error);
    }
  };
  fetchLevelCounts();
}, [userId, packages]);

  const handleCopyReferral = async () => {
    const link = `${window.location.origin}?ref=${userId}`;
    const success = await copyToClipboard(link);
    addToast(success ? 'success' : 'error', success ? 'Referral link copied!' : 'Failed to copy');
  };
  

  const handleClaim = async () => {
    if (isClaiming || parseFloat(claimableIncome.totalClaimable) <= 0) return;
    
    setIsClaiming(true);
    try {
      const amount = parseFloat(claimableIncome.totalClaimable);
      const result = await claimAllIncome(amount, userId);
      if (result.success) {
        addToast('success', 'Income claimed successfully!');
        setTimeout(onRefresh, 2000);
      } else {
        addToast('error', result.error || 'Claim failed');
      }
    } catch (error) {
      addToast('error', 'Claim failed');
    } finally {
      setIsClaiming(false);
    }
  };

  if (isLoading) {
    return <Loader message="Processing..." />;
  }

  return (
    <div className="dashboard">
      {/* User Info Section */}
      <section className="user-info-section animate-fadeInUp">
        <div className="user-ids">
          <div className="id-box">
            <span className="id-label">Your ID</span>
            <span className="id-value">{formatUserId(userId)}</span>
          </div>
          <div className="id-divider">
            
        <img src="/assets/gifs/logo.png" alt="Packages" className="header-gif" />
      
          </div>
          <div className="id-box">
            <span className="id-label">Upline ID</span>
            <span className="id-value">{formatUserId(userData.referrer)}</span>
          </div>
        </div>
        
        {highestPackage && (
  <div 
    className={`current-unit package-tier-${highestPackage.id}`}
    style={{ '--package-color': PACKAGE_INFO[highestPackage.id].color }}
  >
    <span className="unit-icon animate-pulse">{PACKAGE_INFO[highestPackage.id].icon}</span>
    <span className="unit-name">{PACKAGE_INFO[highestPackage.id].name}</span>
  </div>
)}
      </section>

      {/* Stats Grid */}
      <section className="stats-section animate-fadeInUp delay-100" data-tour="stats">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon-wrap">
              <span className="stat-emoji animate-bounce-slow">💰</span>
            </div>
            <span className="stat-label">TOTAL INCOME</span>
            <span className="stat-value">${formatNumber(userData.totalIncome)}</span>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon-wrap">
              <span className="stat-emoji animate-pulse">👥</span>
            </div>
            <span className="stat-label">TOTAL TEAM</span>
            <span className="stat-value">{totalTeam}</span>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon-wrap">
              <span className="stat-emoji animate-shake">🎯</span>
            </div>
            <span className="stat-label">DIRECT COUNT</span>
            <span className="stat-value">{userData.totalDirectReferrals}</span>
          </div>
          
          <div className="stat-card highlight">
            <div className="stat-icon-wrap highlight-icon">
              <span className="stat-emoji animate-glow">⚡</span>
            </div>
            <span className="stat-label">AVAILABLE</span>
            <span className="stat-value">${formatNumber(claimableIncome.totalClaimable)}</span>
          </div>
        </div>
      </section>

      {/* Claim Section */}
      <section className="claim-section animate-fadeInUp delay-200" data-tour="withdraw">
        <button 
          className="claim-btn"
          onClick={handleClaim}
          disabled={isClaiming || parseFloat(claimableIncome.totalClaimable) <= 0}
        >
          {isClaiming ? (
            <>
              <span className="btn-spinner"></span>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span className="claim-icon animate-bounce-slow">💎</span>
              <span>Withdraw ${formatNumber(claimableIncome.totalClaimable)}</span>
            </>
          )}
        </button>
      </section>

      {/* Referral Link */}
      <section className="referral-section animate-fadeInUp delay-300">
        <div className="referral-header">
          <div className="referral-gif-wrap">
            <img src="/assets/gifs/ref.gif" alt="Referral" className="referral-gif" />
          </div>
          <h3>Referral Link</h3>
        </div>
        <div className="referral-box">
          <input 
            type="text" 
            value={`${window.location.origin}?ref=${userId}`}
            readOnly 
            className="referral-input"
          />
          <button className="copy-btn" onClick={handleCopyReferral}>
            📋
          </button>
        </div>
      </section>

      {/* Income Breakdown with Tabs */}
      <section className="income-section animate-fadeInUp delay-400">
        <div className="income-tabs">
          <button 
            className={`income-tab ${incomeTab === 'total' ? 'active' : ''}`}
            onClick={() => setIncomeTab('total')}
          >
            <span className="tab-icon">📊</span>
            Total Income
          </button>
          <button 
            className={`income-tab ${incomeTab === 'available' ? 'active' : ''}`}
            onClick={() => setIncomeTab('available')}
          >
            <span className="tab-icon">⚡</span>
            Available Income
          </button>
        </div>

        {incomeTab === 'total' ? (
          <div className="income-content">
            <div className="income-total">
              <span className="income-total-label">Total Earned</span>
              <span className="income-total-value">${formatNumber(userData.totalIncome)}</span>
            </div>
            <div className="income-grid">
              <div className="income-item">
                <span className="income-icon animate-bounce-slow">🎯</span>
                <div className="income-details">
                  <span className="income-label">Direct Referral</span>
                  <span className="income-value">${formatNumber(incomeBreakdown.directReferralIncome)}</span>
                </div>
              </div>
              <div className="income-item">
                <span className="income-icon animate-pulse">📈</span>
                <div className="income-details">
                  <span className="income-label">Level Income</span>
                  <span className="income-value">${formatNumber(incomeBreakdown.levelIncome)}</span>
                </div>
              </div>
              <div className="income-item">
                <span className="income-icon animate-shake">🌊</span>
                <div className="income-details">
                  <span className="income-label">Pool Income</span>
                  <span className="income-value">${formatNumber(incomeBreakdown.poolIncome)}</span>
                </div>
              </div>
              <div className="income-item">
                <span className="income-icon animate-glow">👑</span>
                <div className="income-details">
                  <span className="income-label">Royalty Income</span>
                  <span className="income-value">${formatNumber(incomeBreakdown.royaltyIncome)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="income-content available-content">
            <div className="income-total available-total-box">
              <span className="income-total-label">Available to Withdraw</span>
              <span className="income-total-value available-amount">${formatNumber(claimableIncome.totalClaimable)}</span>
            </div>
            <div className="income-grid">
              <div className="income-item">
                <span className="income-icon animate-pulse">💵</span>
                <div className="income-details">
                  <span className="income-label">Regular Income</span>
                  <span className="income-value available-value">${formatNumber(claimableIncome.regularIncome)}</span>
                </div>
              </div>
              <div className="income-item">
                <span className="income-icon animate-shake">🌊</span>
                <div className="income-details">
                  <span className="income-label">Pool Income</span>
                  <span className="income-value available-value">${formatNumber(claimableIncome.poolIncome)}</span>
                </div>
              </div>
              <div className="income-item">
                <span className="income-icon animate-glow">👑</span>
                <div className="income-details">
                  <span className="income-label">Royalty Income</span>
                  <span className="income-value available-value">${formatNumber(claimableIncome.royaltyIncome)}</span>
                </div>
              </div>
              <div className="income-item">
                <span className="income-icon animate-bounce-slow">✅</span>
                <div className="income-details">
                  <span className="income-label">Total Claimed</span>
                  <span className="income-value">${formatNumber(userData.totalClaimed)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Eligible Pools */}
      {(Object.keys(poolEligibility).length > 0 || royaltyEligible) && (
        <section className="pools-section animate-fadeInUp delay-500">
          <div className="section-header">
            <h3><span className="header-icon animate-shake">🏊</span> Eligible Pools</h3>
          </div>
          <div className="pools-grid">
            {Object.entries(poolEligibility).map(([pkgId, data]) => (
              <div key={pkgId} className="pool-card">
                <div className="pool-icon animate-pulse">{PACKAGE_INFO[pkgId]?.icon}</div>
                <div className="pool-info">
                  <span className="pool-name">{PACKAGE_INFO[pkgId]?.name} Pool</span>
                  <span className="pool-claimable">${formatNumber(data.claimable)}</span>
                </div>
                <span className="pool-badge">✓</span>
              </div>
            ))}
            {royaltyEligible && (
              <div className="pool-card royalty">
                <div className="pool-icon animate-glow">👑</div>
                <div className="pool-info">
                  <span className="pool-name">Royalty Pool</span>
                  <span className="pool-claimable">${formatNumber(claimableIncome.royaltyIncome)}</span>
                </div>
                <span className="pool-badge">✓</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recent Activities */}
      <section className="activities-section animate-fadeInUp delay-500" data-tour="activities">
        <div className="section-header">
          <h3><span className="header-icon animate-bounce-slow">📜</span> Recent Activities</h3>
          {activities.length > 5 && (
            <button className="view-all-btn" onClick={onShowActivities}>
              View All
            </button>
          )}
        </div>
        
        {recentActivities.length > 0 ? (
          <div className="activities-list">
            {recentActivities.map((activity) => {
              const info = ACTIVITY_TYPES[activity.activityType] || { name: 'Activity', icon: '📌', color: '#00ff88' };
              return (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon" style={{ background: info.color }}>
                    {info.icon}
                  </div>
                  <div className="activity-content">
                    <span className="activity-type">{info.name}</span>
                    {parseFloat(activity.amount) > 0 && (
                      <span className="activity-amount">${formatNumber(activity.amount)}</span>
                    )}
                  </div>
                  <span className="activity-time">{formatTimeAgo(activity.timestamp)}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-activities">
            <span className="no-activities-icon">🕐</span>
            <p>No activities yet</p>
          </div>
        )}
      </section>

      {/* Telegram CTA */}
        <section className="telegram-cta animate-fadeInUp delay-500" data-tour="telegram">
          <button className="telegram-btn" onClick={onShowTelegram}>
            <div className="tg-gif-container">
              <img src="/assets/gifs/t2.gif" alt="Telegram" className="tg-gif" />
            </div>
            <div className="tg-content">
              <span className="tg-title">Get Telegram Notifications</span>
              <span className="tg-desc">Instant alerts for all activities</span>
            </div>
            <span className="tg-arrow">→</span>
          </button>
          
          <button className="tg-big-gif-box" onClick={onShowTelegram}>
            <img src="/assets/gifs/t.gif" alt="Telegram" className="tg-big-gif" />
          </button>
        </section>
    </div>
  );
};

export default Dashboard;
